declare var ko: any;

export interface AuthorDetails {
    authorName : string|null,
    authorPicture : string|null,
    authorUrl : string|null,
}

export interface StylesOptions {
    primaryColor : string|null,
    secondaryColor : string|null,
}
/**
 * A builder class for creating customizable HTML content for newsletter articles.
 * Provides methods for crafting different parts of an article, such as title, excerpt,
 * featured image, and more, based on configurable modes (regular or custom).
 */
export default class NewsletterArticlePickerBuilder {

    static REGULAR_MODE = 'regular';
    static CUSTOM_CONTENT_MODE = 'Custom Content';
    static CUSTOM_HEADER_CONTENT_FOOTER_MODE = 'Custom Header/Content/Footer';
    static CUSTOM_KEYS_ORDER = ['title', 'author', 'datePublished', 'excerpt', 'featuredImage', 'content', 'readMoreLink'];
    private _htmlString: string;
    private _htmlCollection: Record<string, string>;
    private _vm: any;
    private readonly _randomId : string;

    private _hasTitle: boolean = false;
    private _hasReadMore: boolean = false;
    private _mode : string = NewsletterArticlePickerBuilder.REGULAR_MODE;
    private readonly _primaryColor : string|null = null;
    private readonly _secondaryColor : string|null = null;

    constructor(
        mode: string = NewsletterArticlePickerBuilder.REGULAR_MODE,
        options: StylesOptions = {primaryColor: '#000000', secondaryColor: '#000000'}
    ) {
        const randomIdentifier = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

        this._htmlString = '';
        this._htmlCollection = {};
        this._vm = {
            randomId: ko.observable(randomIdentifier),
            addExcerpt: ko.observable(true),
            addContent: ko.observable(true),
            addFeaturedImage: ko.observable(true),
            addAuthor: ko.observable(true),
            addAuthorImage: ko.observable(true),
            addCommentCount: ko.observable(true),
            addReadMoreLink: ko.observable(true),
            lastKey: ko.observable(null),
            primaryColor: ko.observable(options.primaryColor),
            secondaryColor: ko.observable(options.secondaryColor),

            title: ko.observable(null),
            permalink: ko.observable(null),
            excerpt: ko.observable(null),
            featuredImage: ko.observable(null),
            content: ko.observable(null),
            authorName: ko.observable(null),
            authorUrl: ko.observable(null),
            authorPicture: ko.observable(null),
            datePublished: ko.observable(null),
            commentCount: ko.observable(null),
            readMoreLink: ko.observable(null)
        };

        this._randomId = randomIdentifier;
        this._mode = mode;
        this._primaryColor = options.primaryColor;
        this._secondaryColor = options.secondaryColor;
    }

    get randomId(): string {
        return this._randomId;
    }

    get primaryColor(): string|null {
        return this._primaryColor;
    }

    get secondaryColor(): string|null {
        return this._secondaryColor;
    }
    get htmlString(): string {
        return this._htmlString;
    }

    set htmlString(value: string) {
        this._htmlString = value;
    }

    get htmlCollection(): Record<string, string>
    {
        return this._htmlCollection;
    }

    set htmlCollection(value: Record<string, string>) {
        this._htmlCollection = value;
    }

    get vm(): any {
        return this._vm;
    }

    set vm(value: any) {
        this._vm = value;
    }

    get hasTitle(): boolean {
        return this._hasTitle;
    }

    set hasTitle(value: boolean) {
        this._hasTitle = value;
    }
    get hasReadMore(): boolean {
        return this._hasReadMore;
    }
    set hasReadMore(value: boolean) {
        this._hasReadMore = value;
    }

    get mode(): string {
        return this._mode;
    }

    set mode(value: string) {
        this._mode = value;
    }


    /**
     * Creates and sets the HTML markup for an article title based on the provided title and permalink.
     *
     * @param {string} title - The text content of the title.
     * @param {string} permalink - The URL the title links to.
     * @return {NewsletterArticlePickerBuilder} Returns the current instance for method chaining.
     */
    createTitle(title: string, permalink: string): NewsletterArticlePickerBuilder {
        if (!title || !permalink) {
            return this;
        }
        this.vm.title(title);
        this.vm.permalink(permalink);

        let titleHtml = `<h2><a data-bind="text:title, attr: {href:permalink}" class="disable"></a></h2>`;

        if(this.mode === NewsletterArticlePickerBuilder.CUSTOM_CONTENT_MODE){
            titleHtml = `
            <table role="presentation" width="530" cellspacing="0" cellpadding="0" border="0">
                <tr>
                    <td data-bind="style: { paddingBottom: (lastKey() === 'title' ? '0px' : '16px') }">
                        <h2 style="margin:0; font-size:20px;" data-bind="text:title, style: { color: '#333' }"></h2>
                    </td>
            </table>
            `;
        }
        this.htmlCollection['title'] = titleHtml;
        this.hasTitle = true;
        return this;
    }

    /**
     * Creates and sets an HTML excerpt based on the provided text and the current mode.
     *
     * @param {string} excerpt - The text content to include in the excerpt.
     * If the value is empty or not provided, the method returns early without modifying the excerpt HTML.
     * @return {NewsletterArticlePickerBuilder} The current instance of the class to allow method chaining.
     */
    createExcerpt(excerpt: string): NewsletterArticlePickerBuilder {
        if (!excerpt) {
            return this;
        }

        this.vm.excerpt(excerpt);

        const checkBoxInputExcerpt : string = `
        <div class="form-check-inline" data-display="false">
            <input type="checkbox" data-bind="checked:addExcerpt, attr:{ id: 'ce-'+ randomId() }">
            <label class="icon i-check" data-bind="attr:{ for: 'ce-'+ randomId() }">Include Excerpt</label>
        </div>
        `;
        let excerptHtml = `
        ${checkBoxInputExcerpt}
        <p data-bind="text:excerpt, attr:{'data-display':addExcerpt() ? 'true' : 'false'}"></p>
        <br data-bind="attr:{'data-display':addExcerpt() ? 'true' : 'false'}">`;

        if(this.mode === NewsletterArticlePickerBuilder.CUSTOM_CONTENT_MODE){
            excerptHtml = `
            ${checkBoxInputExcerpt}
            <p data-bind="text:excerpt, style: { paddingBottom: (lastKey() === 'excerpt' ? '0px' : '16px'), margin: 0 }, attr:{'data-display':addExcerpt() ? 'true' : 'false'}"></p>`;
        }

        this.htmlCollection['excerpt'] = excerptHtml;
        return this;
    }

    /**
     * Creates and sets the HTML for a featured image based on the provided image data and permalink.
     *
     * @param {string} image - The image data, expected to include 'altText', 'height', 'url', and 'width'.
     * @param {string} permalink - The URL that the image links to.
     * @return {NewsletterArticlePickerBuilder} The current instance of the class for method chaining.
     */
    createFeaturedImage(image: string, permalink: string): NewsletterArticlePickerBuilder {
        if (!image || !permalink) {
            return this;
        }

        this.vm.featuredImage(image);
        this.vm.permalink(permalink);

        const checkBoxInputFeaturedImage : string = `
        <div class="form-check-inline" data-display="false">
            <input type="checkbox" data-bind="attr: {id : 'cfi-' + randomId() } ,checked:addFeaturedImage">
            <label class="icon i-check" data-bind="attr: {for : 'cfi-' + randomId() }">Include Featured Image</label>
        </div>
        `;
        let featuredImageHtml = `
        ${checkBoxInputFeaturedImage}
        <div class="custom-anchor" data-bind="attr:{'data-display':addFeaturedImage() ? 'true' : 'false'}">
            <a class="disable" rel="noreferrer noopener" target="_blank" data-bind="attr:{ href: permalink }">
                <img data-bind="attr:{src: featuredImage().url, alt: featuredImage().altText, height:featuredImage().height, width:featuredImage().width }" alt="">
            </a>
        </div>
        <br data-bind="attr:{'data-display':addFeaturedImage() ? 'true' : 'false'}">`;

        if(this.mode === NewsletterArticlePickerBuilder.CUSTOM_CONTENT_MODE){
            featuredImageHtml = `
            ${checkBoxInputFeaturedImage}
            <table role="presentation" width="530" cellspacing="0" cellpadding="0" border="0" data-bind="attr:{'data-display':addFeaturedImage() ? 'true' : 'false'}">
                <tr>
                    <td align="center" data-bind="style: { paddingBottom: (lastKey() === 'featuredImage' ? '0px' : '16px') }">
                        <a data-bind="attr:{ href: permalink }" class="disable" target="_blank">
                        <img data-bind="attr:{src: featuredImage().url, alt: featuredImage().altText }" width="530" alt=""
                            style="display:block; border:0; border-radius:10px; max-width:530px; height:auto;">
                        </a>
                    </td>
                </tr>
            </table>
            `;
        }
        this.htmlCollection['featuredImage'] =  featuredImageHtml;
        return this;
    }

    /**
     * Generates and adds the content HTML structure to the HTML collection.
     * The HTML structure varies depending on the mode of the picker.
     *
     * @param {string} content - The content to be included in the generated HTML.
     * @return {NewsletterArticlePickerBuilder} The current instance of the class for method chaining.
     */
    createContent(content: string): NewsletterArticlePickerBuilder {
        if (!content) {
            return this;
        }
        this.vm.content(content);
        const checkBoxInputContent: string = `
        <div class="form-check-inline" data-display="false">
            <input type="checkbox" data-bind="attr:{id : 'cc-'+ randomId() },checked: addContent">
            <label class="icon i-check" data-bind="attr:{for : 'cc-'+ randomId() }">Include Content</label>
        </div>
        `;

        let contentHtml = `
        ${checkBoxInputContent}
        <br data-bind="attr:{'data-display':addContent() ? 'true' : 'false'}">
        <p data-bind="html:content, attr:{'data-display': addContent() ? 'true' : 'false'}"></p>
        <br data-bind="attr:{'data-display': addContent() ? 'true' : 'false'}">`;

        if(this.mode === NewsletterArticlePickerBuilder.CUSTOM_CONTENT_MODE){
            content = this.addStyleToAnchorTags(content, this.primaryColor);
            this.vm.content(content);
            contentHtml = `
            ${checkBoxInputContent}
            <p data-bind="html:content, style: { paddingBottom: (lastKey() === 'content' ? '0px' : '16px'), margin: 0 }, attr:{'data-display':addContent() ? 'true' : 'false'}"></p>`;
        }
        this.htmlCollection['content'] = contentHtml;
        return this;
    }

    /**
     * Modifies the style of all anchor tags within the given HTML string by applying a specified color.
     *
     * @param {string} html - The HTML string containing the anchor tags to be styled.
     * @param {string} color - The color to apply to the anchor tags' text.
     * @return {string} - The modified HTML string with updated anchor tag styles.
     */
    public addStyleToAnchorTags(html: string, color : string): string {

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const anchorTags = tempDiv.querySelectorAll('a');

        anchorTags.forEach(anchor => {
            anchor.style.color = color;
        });

        return tempDiv.innerHTML;

    }

    /**
     * Generates and assigns a "Read More" link in HTML format based on the provided permalink.
     * The HTML structure of the link will vary depending on the mode.
     *
     * @param {string} permalink - The URL to be used for the "Read More" link. If not provided, the method will return the current instance without adding the link.
     * @return {NewsletterArticlePickerBuilder} The current instance of the class with the "Read More" link added to the HTML collection and the `hasReadMore` property set to true.
     */
    createReadMoreLink(permalink: string): NewsletterArticlePickerBuilder {
        if (!permalink) {
            return this;
        }
        this.vm.permalink(permalink);

        const checkBoxInputReadMore: string = `
        <div class="form-check-inline" data-display="false">
            <input type="checkbox" data-bind="attr:{ id: 'cerml-' + randomId() },checked:addReadMoreLink">
            <label data-bind="attr:{ for: 'cerml-' + randomId() }" class="icon i-check">Include Read More</label>
        </div>
        `;

        let readMoreLinkHtml = `
        ${checkBoxInputReadMore}
        <span class="affiliate-single"
            data-bind="attr:{ 'data-display':addReadMoreLink() ? 'true' : 'false'}">
            <a class="disable" data-bind="attr:{ href: permalink }" rel="noreferrer noopener" target="_blank">Read More &raquo;</a>
        </span>`;

        if(this.mode === NewsletterArticlePickerBuilder.CUSTOM_CONTENT_MODE){
            readMoreLinkHtml = `
            ${checkBoxInputReadMore}
            <table role="presentation" width="530" cellspacing="0" cellpadding="0" border="0" data-bind="attr:{'data-display':addReadMoreLink() ? 'true' : 'false'}">
                <tr>
                    <td align="right" style="text-align: right;" data-bind="style: { paddingBottom: (lastKey() === 'readMoreLink' ? '0px' : '16px') }">
                    <a
                        data-bind="attr:{ href: permalink },
                        style: { color: primaryColor, backgroundColor: '#ffffff', border: '2px solid ' + primaryColor(), padding: '10px 20px', textDecoration: 'none',
                        display: 'inline-block', width: '180px', minWidth: '180px', textAlign: 'center', borderRadius: '30px', fontWeight: 'bold' }">
                        Read More &raquo;
                        </a>
                    </td>
                </tr>
            </table>
            `;
        }
        this.htmlCollection['readMoreLink'] = readMoreLinkHtml;
        this.hasReadMore = true;
        return this;
    }
    /**
     * Creates and assigns the author HTML structure to the HTML collection.
     * The method generates the author's details (name, picture, URL, and published date).
     *
     * @param {AuthorDetails} authorDetails
     * @param {string} datePublished - The published date of the article.
     * @return {NewsletterArticlePickerBuilder} The current instance of the class with the author details added to the HTML collection.
     */
    createAuthor(authorDetails: AuthorDetails, datePublished: string) : NewsletterArticlePickerBuilder{
        const { authorName, authorPicture, authorUrl } = authorDetails;
        datePublished = NewsletterArticlePickerBuilder.formatDateString(datePublished);
        return this.generateAuthorHtml(authorName, authorUrl, authorPicture, datePublished);
    }

    /**
     * Generates and assigns the HTML structure for the author details.
     * Adds the author's name, picture, and published date into the HTML collection.
     *
     * @param {string} authorName - Name of the author.
     * @param {string} authorUrl - URL to the author's profile.
     * @param {string} authorPicture - URL of the author's picture.
     * @param {string} datePublished - The published date of the article.
     * @return {NewsletterArticlePickerBuilder} The current instance of the class with the author details added to the HTML collection.
     */
    private generateAuthorHtml(authorName: string, authorUrl: string, authorPicture: string, datePublished: string): NewsletterArticlePickerBuilder {
        if (!authorName || !authorUrl) {
            return this;
        }
        this.vm.authorName(authorName);
        this.vm.authorUrl(authorUrl);
        this.vm.authorPicture(authorPicture);
        this.vm.datePublished(datePublished);

        let authorHtml: string;

        const checkBoxInputAuthor:string = `
        <div class="form-check-inline" data-display="false">
            <input type="checkbox" data-bind="attr:{ id : 'ca-' + randomId() }, checked:addAuthor">
            <label for="ca-${this.randomId}" class="icon i-check" data-bind="attr:{ for : 'ca-' + randomId() }">Include Author</label>
        </div>
        `;

        if (this.mode === NewsletterArticlePickerBuilder.CUSTOM_CONTENT_MODE) {
            authorHtml = `
            ${checkBoxInputAuthor}
            <table role="presentation" width="530" cellspacing="0" cellpadding="0" border="0" data-bind="attr:{'data-display':addAuthor() ? 'true' : 'false'}">
                <tr>`;
            if (authorPicture) {
                    authorHtml += `
                    <td style="padding-bottom: 16px; width:50px; text-align:center; vertical-align:middle;">
                        <a data-bind="attr:{ href : authorUrl }" target="_blank">
                            <img data-bind="attr: {src : authorPicture, alt: authorName }" width="40" style="display:block; border:0; border-radius:40px; max-width:40px; height:auto;" alt="" src="">
                        </a>
                    </td>`;
                }
            authorHtml += `
                    <td style="padding-bottom: 16px; vertical-align:middle; font-size:14px; color:#666666;line-height: 16px">
                        By <a data-bind="text: authorName, attr:{ href : authorUrl },style: { color: primaryColor, textDecoration: 'none', fontWeight: 'bold'}"></a>
                        ${datePublished ? `<br>${datePublished}` : ""}
                    </td>
                </tr>
            </table>`;
        } else {
            authorHtml = `
            ${checkBoxInputAuthor}
            <p data-bind="attr:{'data-display':addAuthor() ? 'true' : 'false'}">
                By <a data-bind="text: authorName, attr:{ href : authorUrl }" rel="noreferrer noopener" target="_blank"></a>
                <a data-bind="attr:{href : authorUrl }" target="_blank">
                    <img data-bind="attr:{ src: authorUrl, alt: authorName }" width="40" style="display:block; border:0; border-radius:40px; max-width:40px; height:auto;"  alt="">
                </a>
            </p>
            <br data-bind="attr:{'data-display':addAuthor() ? 'true' : 'false'}">`;

            if (datePublished) {
                authorHtml += `
                <p data-bind="text :datePublished, attr:{'data-display':addAuthor() ? 'true' : 'false'}"></p>
            <br>`;
            }
        }

        this.htmlCollection['author'] = authorHtml;
        return this;
    }

    /**
     * Builds the complete article preview HTML string using the collected elements.
     * The method ensures that both the title and read-more link are present before building.
     * Depending on the mode, it structures the HTML using the custom order or iterates through the collection keys.
     * If the HTML string is successfully created, it finalizes the structure and binds the view model to the element.
     *
     * @throws {Error} If the title or read-more link is missing.
     */
    buildArticlePreview(): void {
        if (!this.hasTitle) {
            console.error('Article must have a title and a read more link to be built');
            return;
        }

        if(this.mode === NewsletterArticlePickerBuilder.CUSTOM_CONTENT_MODE){
            NewsletterArticlePickerBuilder.CUSTOM_KEYS_ORDER.forEach(key => {
                if (this.htmlCollection[key]) {
                    this.htmlString += this.htmlCollection[key];
                    this.vm.lastKey(key);
                }
            });
        }else{
            for (const key in this.htmlCollection) {
                const element = this.htmlCollection[key];
                this.htmlString += element;
            }
        }

        if(this.htmlString !== ''){
            if(this.mode === NewsletterArticlePickerBuilder.CUSTOM_CONTENT_MODE){
                this.htmlString =
                    `<div id="${this.randomId}" class="article-nl-card">
                        <table role="presentation" width="560" cellspacing="0" cellpadding="0" border="0" style="padding:15px; border-radius:10px; border:1px solid #dcdcdc; margin-bottom: 16px">
                            <tr>
                                <td>${this.htmlString}</td>
                            </tr>
                        </table>
                    </div>`;
            }else {
                this.htmlString = `<div id="${this.randomId}" class="article-nl-card">${this.htmlString}</div>`;
            }
            this.bindViewModelToElement();
        }
    }

    /**
     * Binds the view model to the corresponding element in the DOM.
     * Uses Knockout.js to apply bindings to an element identified by `randomId`.
     * Ensures the bindings are applied after the DOM is updated.
     * Logs an error if the element cannot be found.
     */
    private bindViewModelToElement(): void {
        setTimeout(() => {
            const element = document.getElementById(this.randomId);
            if (element) {
                ko.applyBindings(this.vm, element);
            } else {
                console.error('Element with ID', this.randomId, 'not found for applying bindings');
            }
        }, 0);
    }

    /**
     * Converts a date string into a formatted date string.
     * The method transforms a date string into the format "Month Day, Year" (e.g., "Jan 1, 2023").
     *
     * @param {string} dateString - The input date string in a standard date format.
     * @return {string} The formatted date string in "Month Day, Year" format.
     */
    private static formatDateString(dateString: string): string {
        let date = new Date(dateString);

        if (isNaN(date.getTime())) {
            date = new Date();
            console.error('Invalid date string', dateString, 'provided. Using current date instead.');
        }

        const options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };
        return new Intl.DateTimeFormat("en-US", options).format(date);
    }

    /**
     * Refines the provided HTML string by removing elements with specific data attributes
     * and cleaning up unnecessary attributes from other elements.
     *
     * @param {string} html - The HTML string to be processed.
     * @return {string} - The refined and cleaned-up HTML as a string.
     */
    public static refineHtml(html: string): string {
        NewsletterArticlePickerBuilder.removeBindings(html);
        const container = document.createElement('div');
        container.innerHTML = html;
        container
            .querySelectorAll('[data-display="false"]')
            .forEach((element) => element.remove());
        container
            .querySelectorAll('[data-bind], [data-display]')
            .forEach((element) => {
                element.removeAttribute('data-bind');
                element.removeAttribute('data-display');
            });
        return container.innerHTML.trim();
    }

    /**
     * Removes Knockout bindings from the provided HTML element.
     *
     * @param {string} html - The HTML element or string representing the element from which Knockout bindings should be removed.
     * @return {void}
     */
    private static removeBindings(html: string): void {
        if (ko.dataFor(html)) {
            ko.cleanNode(html);
        }
    }
}



// WEBPACK FOOTER //
// ./js/utils/NewsletterArticlePickerBuilder.ts